import {
    getCompanySlugPublicCareerJobsIdUploadUrl,
    getCompanySlugPublicCareerTalentPoolUploadUrl,
    getStorageAccess,
    getStorageUpload,
} from '@/generated/api-client'
import { configureOpenApiClient } from '@/lib/api/client/configure-openapi-client'

configureOpenApiClient()

function resolveApiBaseURL(): string {
    if (process.env.NEXT_PUBLIC_API_URL) {
        return process.env.NEXT_PUBLIC_API_URL
    }
    if (typeof window !== 'undefined') {
        return '/api'
    }
    const appBase = process.env.NEXT_PUBLIC_APP_BASE_URL || 'http://localhost:3000'
    return `${appBase.replace(/\/$/, '')}/api`
}

/** Mint a short-lived signed read URL for an existing storage object. */
export async function fetchStorageAccessUrl(fileUrl: string): Promise<string> {
    const { data } = await getStorageAccess({
        query: { fileUrl },
        baseURL: resolveApiBaseURL(),
        throwOnError: true,
    })
    return data.url
}

export type StorageUploadKind = 'document' | 'introVideo'

/** Anonymous upload target: a public job's apply page, or the company's talent-pool apply page. */
export type PublicUploadTarget = { publicJobPostingId: number } | { talentPool: true }

export type StorageUploadCredentialInput = {
    companySlug: string
    kind: StorageUploadKind
    fileExtension: string
    contentType?: string
    /** Anonymous pages: upload through that page's public endpoint instead of the signed-in one. */
    publicTarget?: PublicUploadTarget
}

export type StorageUploadCredential = {
    uploadUrl: string
    fileUrl: string
}

/** Mint signed upload credentials (`uploadUrl` + canonical `fileUrl`) — the server picks the object name. */
export async function fetchStorageUploadCredential(input: StorageUploadCredentialInput): Promise<StorageUploadCredential> {
    const query = { kind: input.kind, fileExtension: input.fileExtension, contentType: input.contentType }
    const { publicTarget } = input
    const { data } =
        publicTarget && 'publicJobPostingId' in publicTarget
            ? await getCompanySlugPublicCareerJobsIdUploadUrl({
                  path: { companySlug: input.companySlug, id: String(publicTarget.publicJobPostingId) },
                  query,
                  baseURL: resolveApiBaseURL(),
                  throwOnError: true,
              })
            : publicTarget
              ? await getCompanySlugPublicCareerTalentPoolUploadUrl({
                    path: { companySlug: input.companySlug },
                    query: { fileExtension: input.fileExtension, contentType: input.contentType },
                    baseURL: resolveApiBaseURL(),
                    throwOnError: true,
                })
              : await getStorageUpload({
                    query: { companySlug: input.companySlug, ...query },
                    baseURL: resolveApiBaseURL(),
                    throwOnError: true,
                })
    return {
        uploadUrl: data.uploadUrl,
        fileUrl: data.fileUrl,
    }
}
